import { api, type GoogleIntegrationStatusData } from "./api";
import { GOOGLE_SCOPE_BUNDLES } from "./googleScopes";

export type GoogleConsentScopeStatus = {
  granted: boolean;
  connectionStatus: string;
  missingScopes: string[];
};

export type GoogleConsentStatus = {
  calendarToday: GoogleConsentScopeStatus;
};

type GoogleConsentStatusDebugScope = {
  ok: boolean;
  granted?: boolean;
  connectionStatus?: string;
  missingScopes?: string[];
  error?: {
    code?: string;
    message?: string;
  };
};

type GoogleConsentStatusDebug = {
  timestamp: string;
  calendarToday: GoogleConsentStatusDebugScope;
};

function toGoogleConsentScopeStatus(status: GoogleIntegrationStatusData): GoogleConsentScopeStatus {
  return {
    granted: status.scopeBundleGranted === true,
    connectionStatus: status.connectionStatus ?? "",
    missingScopes: Array.isArray(status.missingScopes) ? status.missingScopes : [],
  };
}

function createDefaultGoogleConsentScopeStatus(): GoogleConsentScopeStatus {
  return {
    granted: false,
    connectionStatus: "",
    missingScopes: [],
  };
}

function toErrorDebug(error: unknown): GoogleConsentStatusDebugScope["error"] {
  if (!error || typeof error !== "object") {
    return {
      message: typeof error === "string" ? error : undefined,
    };
  }

  const candidate = error as { code?: unknown; message?: unknown };
  return {
    code: typeof candidate.code === "string" ? candidate.code : undefined,
    message: typeof candidate.message === "string" ? candidate.message : undefined,
  };
}

function logGoogleConsentStatusDebug(debug: GoogleConsentStatusDebug) {
  if (typeof window !== "undefined") {
    (window as any).__wizmateGoogleConsentStatusDebug = debug;
  }
}

export async function getGoogleConsentStatus(): Promise<GoogleConsentStatus> {
  const calendarTodayStatusResult = await api
    .getGoogleIntegrationStatus(GOOGLE_SCOPE_BUNDLES.calendarToday)
    .then((status) => ({ ok: true as const, status }))
    .catch((error) => ({ ok: false as const, error }));
  const calendarTodayStatus =
    calendarTodayStatusResult.ok
      ? toGoogleConsentScopeStatus(calendarTodayStatusResult.status)
      : createDefaultGoogleConsentScopeStatus();

  logGoogleConsentStatusDebug({
    timestamp: new Date().toISOString(),
    calendarToday:
      calendarTodayStatusResult.ok
        ? {
            ok: true,
            granted: calendarTodayStatus.granted,
            connectionStatus: calendarTodayStatus.connectionStatus,
            missingScopes: calendarTodayStatus.missingScopes,
          }
        : {
            ok: false,
            error: toErrorDebug(calendarTodayStatusResult.error),
          },
  });

  return {
    calendarToday: calendarTodayStatus,
  };
}
